import _ from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import { Box, Paper, Button, CircularProgress } from '@mui/material'

import { ChatMessage } from '@/core/models/chat-room.model'
import { useGetChatRoomQuery, useMarkMessageAsReadMutation } from '@/core/services/chat-room'

import { RootState } from '@/redux/rootReducer'
import { skipToken } from '@reduxjs/toolkit/query'

import { isLastMessageFromSender } from '../lib/messageHelpers'
import { Message } from './Message'
import { MessageInput } from './MessageInput'
import { Trans } from '@lingui/macro'

type ChatMessagesProps = {
  messages: ChatMessage[]
  chatRoomId?: string
  onSendMessage: (message: string) => Promise<void>
  onMarkAsRead?: (chatRoomId: string) => Promise<void>
  onLoadMore?: () => void
  isLoadingMore?: boolean
  hasMore?: boolean
}

export function ChatMessages({ messages, chatRoomId, onSendMessage, onMarkAsRead, onLoadMore, isLoadingMore, hasMore }: ChatMessagesProps) {
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })

  const authUser = useSelector((state: RootState) => state.auth.authUser)

  const { data: chatRoom } = useGetChatRoomQuery(chatRoomId ? { chatRoomId } : skipToken)
  const [markMessageAsRead] = useMarkMessageAsReadMutation()

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      const { inlineSize, blockSize } = entry?.contentBoxSize[0] || {}
      if (inlineSize && blockSize) {
        setSize({ width: inlineSize, height: blockSize })
      }
    })
    if (messagesContainerRef.current) {
      observer.observe(messagesContainerRef.current)
    }
    return () => {
      if (messagesContainerRef.current) {
        observer.unobserve(messagesContainerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const element = messagesContainerRef.current
    if (element) {
      element.scrollTop = element.scrollHeight
    }
  }, [messages, messagesContainerRef])

  useEffect(() => {
    if (!chatRoomId || !authUser?._id) {
      return
    }

    const unreadMessages = messages.filter((msg) => {
      const isFromOtherUser = msg.author !== authUser._id
      const isNotReadByMe = !(msg.readBy || []).includes(authUser._id)
      const isUnread = isFromOtherUser && isNotReadByMe

      return isUnread
    })

    const markVisibleMessagesAsRead = _.debounce(async () => {
      for (const msg of unreadMessages) {
        await markMessageAsRead({ chatRoomId, messageId: msg._id })
      }
    }, 500)

    if (unreadMessages.length > 0) {
      markVisibleMessagesAsRead()
    }
  }, [messages, chatRoomId, authUser?._id, markMessageAsRead])

  return (
    <Paper
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        borderRadius: 0,
        boxShadow: 'none',
      }}
    >
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          backgroundColor: 'rgb(247, 239, 239)',
          img: {
            width: size.width * 0.4,
            height: size.height * 0.6,
            objectFit: 'fill',
            overflow: 'auto',
          },
        }}
      >
        {/* Load More Button */}
        {hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Button
              onClick={onLoadMore}
              disabled={isLoadingMore}
              variant="outlined"
              size="small"
              startIcon={isLoadingMore ? <CircularProgress size={16} /> : null}
            >
              <Trans>{isLoadingMore ? 'Loading...' : 'Load More Messages'}</Trans>
            </Button>
          </Box>
        )}

        {Array.isArray(messages) &&
          messages.map((message, index) => (
            <Message
              key={message._id || index}
              chatRoomUsers={chatRoom?.users}
              isLastMessageFromSender={isLastMessageFromSender(messages, index, message.author)}
              message={message}
              size={size}
            />
          ))}
      </Box>
      <MessageInput chatRoomId={chatRoomId} onMarkAsRead={onMarkAsRead} onSendMessage={onSendMessage} />
    </Paper>
  )
}
